import {
  Divider,
  Flex,
  Loader,
  ScrollAreaAutosize,
  Tooltip,
} from "@mantine/core";
import { BoxTitle, Container } from "./StyledComponents";
import styled from "styled-components";
import { theme, media } from "../../theme";
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { useFilterContext } from "../../context/DashboardFilterContext";
import { useEffect, useState, useMemo } from "react";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import isoWeek from "dayjs/plugin/isoWeek"; 
import { getLiveEmployeeDailyDetail } from "../../api/dashboardApi";

dayjs.extend(isoWeek);
dayjs.locale("tr");


type AnalysisTab = "weekly" | "monthly" | "yearly";

const HEAT_COLORS = ["#EAECF0", "#D1FAE5", "#6EE7B7", "#10B981", "#059669", "#064E3B"];
const DAYS_TR = ["Pzt", "Sal", "Çar", "Per", "Cum"];

function minutesToHeatLevel(m: number) {
  if (m === 0) return 0;
  if (m < 120) return 1;
  if (m < 240) return 2;
  if (m < 360) return 3;
  if (m < 480) return 4;
  return 5;
}

function fmtDuration(minutes: number, short = false) {
  if (!minutes || minutes === 0) return short ? "–" : "Gelmedi";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return short 
    ? (h > 0 ? `${h}s ${m}d` : `${m}d`)
    : (h > 0 ? `${h}sa ${m}dk` : `${m}dk`);
}

function buildCalendar(workdays: string[]): { monthGroups: any[] } {
  if (!workdays?.length) return { monthGroups: [] };
  const monthMap: Record<string, string[]> = {};
  
  workdays.forEach((day) => {
    const d = dayjs(day);
    const dow = d.isoWeekday(); 
    if (dow > 5) return; 
    const monthKey = d.format("YYYY-MM");
    if (!monthMap[monthKey]) monthMap[monthKey] = [];
    monthMap[monthKey].push(day);
  });

  return {
    monthGroups: Object.entries(monthMap).map(([key, days]) => {
      const weeks: (string | null)[][] = [];
      let curWeek: (string | null)[] = Array(5).fill(null);
      let lastIsoWeek: number | null = null;

      days.forEach((day) => {
        const d = dayjs(day);
        const currentIsoWeek = d.isoWeek();
        const dowIndex = d.isoWeekday() - 1; 

        if (lastIsoWeek !== null && currentIsoWeek !== lastIsoWeek) {
          weeks.push(curWeek);
          curWeek = Array(5).fill(null);
        }
        curWeek[dowIndex] = day;
        lastIsoWeek = currentIsoWeek;
      });
      if (curWeek.some((x) => x !== null)) weeks.push(curWeek);
      return { monthKey: key, label: dayjs(key + "-01").format("MMM"), weeks };
    }),
  };
}

const WorkDurationCard = ({ flex }: { flex: any }) => {
  const { filters } = useFilterContext();
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calData, setCalData] = useState<any>(null);
  const [selectedEmp, setSelectedEmp] = useState<any>(null);
  const [empSearch, setEmpSearch] = useState("");
  const [clickedDay, setClickedDay] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [analysisTab, setAnalysisTab] = useState<AnalysisTab>("weekly");

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const res: any = await getLiveEmployeeDailyDetail(filters, calYear);
        if (res?.success) setCalData(res.data);
      } catch (err) { console.error(err); }
      setIsLoading(false);
    };
    load();
  }, [filters, calYear]);

  const filteredEmps = (calData?.employees ?? []).filter((e: any) =>
    e.name.toLowerCase().includes(empSearch.toLowerCase())
  );
  
  const activeEmp = selectedEmp ?? filteredEmps[0] ?? null;
  const { monthGroups } = useMemo(() => buildCalendar(calData?.workdays ?? []), [calData]);

  const chartData = useMemo(() => {
    if (!activeEmp || !activeEmp.days) return [];

    if (analysisTab === "weekly") {
      const startOfThisWeek = dayjs().isoWeekday(1); 
      return DAYS_TR.map((label, i) => {
        const dateStr = startOfThisWeek.add(i, "day").format("YYYY-MM-DD");
        return { label, minutes: activeEmp.days[dateStr] ?? 0 };
      });
    }

    if (analysisTab === "monthly") {
      const currentMonthKey = dayjs().format("YYYY-MM");
      const currentMonthData = monthGroups.find((m: any) => m.monthKey === currentMonthKey);
      if (!currentMonthData) return [];

      return currentMonthData.weeks.map((week: (string | null)[], idx: number) => {
        const totalMins = week.reduce((acc: number, dayStr: string | null) => {
          return acc + (dayStr ? (activeEmp.days[dayStr] ?? 0) : 0);
        }, 0);
        return { label: `${idx + 1}. Hafta`, minutes: totalMins };
      });
    }

    if (analysisTab === "yearly") {
      return Array.from({ length: 12 }, (_, i) => {
        const monthKey = dayjs().year(calYear).month(i).format("YYYY-MM");
        const mins = Object.entries(activeEmp.days)
          .filter(([date]) => date.startsWith(monthKey))
          .reduce((acc: number, [, v]: [string, any]) => acc + (v as number), 0);
        return { label: dayjs().month(i).format("MMM"), minutes: mins };
      });
    }
    return [];
  }, [activeEmp, analysisTab, calYear, monthGroups]);

  const maxMins = Math.max(1, ...chartData.map((d: any) => d.minutes));

  return (
    <Container $flex={flex}>
      <HeaderRow>
        <Flex align="center" gap={12}>
          <HeaderIcon>
            <ClockIcon width={20} />
          </HeaderIcon>
          <BoxTitle style={{ fontSize: '16px', fontWeight: 700 }}>Personel Çalışma Analizi</BoxTitle>
        </Flex>
        <YearNav>
          <YearNavBtn onClick={() => setCalYear(y => y - 1)} disabled={calYear <= 2020}>
            <ChevronLeftIcon width={14} />
          </YearNavBtn>
          <YearNavLabel>{calYear}</YearNavLabel>
          <YearNavBtn onClick={() => setCalYear(y => y + 1)} disabled={calYear >= dayjs().year()}>
            <ChevronRightIcon width={14} />
          </YearNavBtn>
        </YearNav>
      </HeaderRow>

      {isLoading ? (
        <Flex h={400} justify="center" align="center"><Loader color="indigo" /></Flex>
      ) : (
        <MainLayout>
          <Sidebar>
            <SearchWrapper>
              <MagnifyingGlassIcon width={16} />
              <input placeholder="Personel ara..." value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} />
            </SearchWrapper>
            <ScrollAreaAutosize mah={550}>
              <Flex direction="column" gap={4}>
                {filteredEmps.map((emp: any) => (
                  <EmpListItem key={emp.id} $active={activeEmp?.id === emp.id} onClick={() => { setSelectedEmp(emp); setClickedDay(null); }}>
                    <EmpAvatar $active={activeEmp?.id === emp.id}>{emp.name[0]}</EmpAvatar>
                    <div className="info">
                      <div className="name">{emp.name}</div>
                      <div className="dept">{emp.department}</div>
                    </div>
                  </EmpListItem>
                ))}
              </Flex>
            </ScrollAreaAutosize>
          </Sidebar>

          <Content>
            {activeEmp ? (
              <>
                <StatBar>
                  <StatItem><div className="val">{fmtDuration(activeEmp.totalMinutes)}</div><div className="lbl">Toplam Çalışma</div></StatItem>
                  <Divider orientation="vertical" h={30} />
                  <StatItem><div className="val">{fmtDuration(activeEmp.avgMinutesPerWorkday)}</div><div className="lbl">Günlük Ortalaması</div></StatItem>
                  <Divider orientation="vertical" h={30} />
                  <StatItem><div className="val">%{Math.round((activeEmp.workdayCount / (calData?.workdays.length || 1)) * 100)}</div><div className="lbl">Devam Oranı</div></StatItem>
                  <TodayBadge>
                    <div className="pulse-dot" />
                    <div className="time">{fmtDuration(activeEmp.days[dayjs().format("YYYY-MM-DD")] ?? 0, true)}</div>
                    <div className="tag">Bugün</div>
                  </TodayBadge>
                </StatBar>

                <HeatmapSection>
                  <ScrollAreaAutosize offsetScrollbars>
                    <div style={{ padding: "4px 0", display: "flex" }}>
                      <DayLabels>{DAYS_TR.map(d => <div key={d}>{d}</div>)}</DayLabels>
                      <MonthsContainer>
                        {monthGroups.map((mg: any, mi: number) => (
                          <MonthGroupBlock key={mi}>
                            <MonthGroupLabel>{mg.label}</MonthGroupLabel>
                            <WeeksRow>
                              {mg.weeks.map((week: (string | null)[], wi: number) => (
                                <WeekColumn key={wi}>
                                  {week.map((day: string | null, di: number) => {
                                    if (!day) return <EmptyCell key={di} />;
                                    const mins = activeEmp.days[day] ?? 0;
                                    return (
                                      <Tooltip key={di} label={`${dayjs(day).format("D MMMM")}: ${fmtDuration(mins)}`} withArrow withinPortal>
                                        <Cell 
                                          $color={HEAT_COLORS[minutesToHeatLevel(mins)]} 
                                          $active={clickedDay?.day === day} 
                                          onClick={() => setClickedDay({ day, minutes: mins })} 
                                        />
                                      </Tooltip>
                                    );
                                  })}
                                </WeekColumn>
                              ))}
                            </WeeksRow>
                          </MonthGroupBlock>
                        ))}
                      </MonthsContainer>
                    </div>
                  </ScrollAreaAutosize>
                  <HeatmapFooter>
                    <div className="legend"><span>Az</span>{HEAT_COLORS.map(c => <div key={c} className="dot" style={{ background: c }} />)}<span>Çok</span></div>
                    {clickedDay && <div className="selected-day"><strong>{dayjs(clickedDay.day).format("D MMMM")}:</strong> {fmtDuration(clickedDay.minutes)}</div>}
                  </HeatmapFooter>
                </HeatmapSection>

                <AnalysisCard>
                  <Flex justify="space-between" align="center" mb={20}>
                    <AnalysisTitle>Detaylı Performans Grafiği</AnalysisTitle>
                    <PeriodToggle>
                      <PeriodButton $active={analysisTab === "weekly"} onClick={() => setAnalysisTab("weekly")}>Bu Hafta</PeriodButton>
                      <PeriodButton $active={analysisTab === "monthly"} onClick={() => setAnalysisTab("monthly")}>Bu Ay</PeriodButton>
                      <PeriodButton $active={analysisTab === "yearly"} onClick={() => setAnalysisTab("yearly")}>Bu Yıl</PeriodButton>
                    </PeriodToggle>
                  </Flex>

                  <MonthGrid>
                    {chartData.map((data: any, i: number) => {
                      const ratio = data.minutes / maxMins;
                      const h = data.minutes > 0 ? Math.max(8, Math.round(ratio * 60)) : 4;
                      return (
                        <MonthCol key={i}>
                          <Tooltip label={`${data.label}: ${fmtDuration(data.minutes)}`} withArrow>
                            <MonthBar $height={h} $hasData={data.minutes > 0} />
                          </Tooltip>
                          <MonthBarLabel>{data.label}</MonthBarLabel>
                          <SlotValue $hasData={data.minutes > 0}>
                            {fmtDuration(data.minutes, true)}
                          </SlotValue>
                        </MonthCol>
                      );
                    })}
                  </MonthGrid>
                </AnalysisCard>
              </>
            ) : <EmptyState>Personel bulunamadı.</EmptyState>}
          </Content>
        </MainLayout>
      )}
    </Container>
  );
};

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  ${media.mobile} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;
const HeaderIcon = styled.div` width: 38px; height: 38px; border-radius: 10px; background: ${theme.colors.brand.normal}10; color: ${theme.colors.brand.normal}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; border: 1px solid ${theme.colors.brand.normal}20; `;
const MainLayout = styled.div`
  display: flex;
  gap: 24px;

  /* Personel listesi (240px) + içerik yan yana 390px'e sığmıyor; alt alta. */
  ${media.mobile} {
    flex-direction: column;
    gap: 16px;
  }
`;
const Sidebar = styled.div`
  width: 240px;
  flex-shrink: 0;

  ${media.mobile} {
    width: 100%;
  }
`;
const SearchWrapper = styled.div` display: flex; align-items: center; gap: 8px; background: #f8fafc; border: 1px solid #e2e8f0; padding: 8px 12px; border-radius: 10px; margin-bottom: 12px; input { border: none; background: transparent; outline: none; font-size: 13px; width: 100%; color: #1e293b; } `;
const EmpListItem = styled.div<{ $active: boolean }>` display: flex; align-items: center; gap: 10px; padding: 10px; border-radius: 10px; cursor: pointer; background: ${p => p.$active ? "#eff6ff" : "transparent"}; border: 1px solid ${p => p.$active ? "#bfdbfe" : "transparent"}; transition: all 0.2s; &:hover { background: #f1f5f9; } .name { font-size: 13px; font-weight: 600; color: ${p => p.$active ? "#1d4ed8" : "#1e293b"}; } .dept { font-size: 11px; color: #64748b; } `;
const EmpAvatar = styled.div<{ $active: boolean }>` width: 32px; height: 32px; border-radius: 50%; background: ${p => p.$active ? "#3b82f6" : "#e2e8f0"}; color: white; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; `;
const Content = styled.div` flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 20px; `;
const StatBar = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  background: #f8fafc;
  padding: 16px 24px;
  border-radius: 16px;
  border: 1px solid #f1f5f9;

  /* Üç istatistik + "Bugün" rozeti tek satırda ~600px istiyordu; sarsın ve
     dikey çizgiler (Divider) satır sonunda anlamsız kalmasın diye gizlensin. */
  ${media.mobile} {
    flex-wrap: wrap;
    gap: 14px;
    padding: 14px 16px;

    .mantine-Divider-root {
      display: none;
    }
  }
`;
const StatItem = styled.div` .val { font-size: 16px; font-weight: 700; color: #1e293b; } .lbl { font-size: 11px; color: #64748b; font-weight: 500; } `;
const TodayBadge = styled.div` margin-left: auto; display: flex; align-items: center; gap: 12px; background: white; padding: 8px 16px; border-radius: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.04); .pulse-dot { width: 8px; height: 8px; border-radius: 50%; background: #22c55e; animation: pulse 2s infinite; } .time { font-size: 15px; font-weight: 700; color: #065f46; } .tag { font-size: 10px; color: #059669; font-weight: 600; text-transform: uppercase; } @keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); } 70% { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); } 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); } } `;
const HeatmapSection = styled.div` background: white; border: 1px solid #e2e8f0; border-radius: 16px; padding: 20px; `;
const DayLabels = styled.div` width: 28px; display: flex; flex-direction: column; gap: 4px; font-size: 9px; color: #94a3b8; font-weight: 600; padding-top: 18px; `;
const MonthsContainer = styled.div`
  display: flex;
  gap: 10px;
`;
const MonthGroupBlock = styled.div` display: flex; flex-direction: column; gap: 4px; `;
const MonthGroupLabel = styled.div` font-size: 10px; font-weight: 600; color: #94a3b8; margin-bottom: 4px; text-transform: capitalize; `;
const WeeksRow = styled.div` display: flex; gap: 4px; `;
const WeekColumn = styled.div` display: flex; flex-direction: column; gap: 4px; `;
const Cell = styled.div<{ $color: string; $active?: boolean }>` width: 14px; height: 14px; border-radius: 3px; background: ${p => p.$color}; cursor: pointer; border: 2px solid ${p => p.$active ? "#3b82f6" : "transparent"}; transition: transform 0.1s; `;
const EmptyCell = styled.div` width: 14px; height: 14px; `;
const HeatmapFooter = styled.div` margin-top: 16px; padding-top: 16px; border-top: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; .legend { display: flex; align-items: center; gap: 6px; span { font-size: 10px; color: #94a3b8; } .dot { width: 12px; height: 12px; border-radius: 2px; } } .selected-day { font-size: 12px; color: #1e293b; background: #f1f5f9; padding: 4px 12px; border-radius: 20px; } `;
const YearNav = styled.div` display: flex; align-items: center; gap: 8px; background: #f1f5f9; padding: 4px 12px; border-radius: 10px; `;
const YearNavBtn = styled.button` border: none; background: transparent; cursor: pointer; color: #64748b; `;
const YearNavLabel = styled.span` font-size: 13px; font-weight: 700; color: #1e293b; `;
const EmptyState = styled.div` height: 300px; display: flex; align-items: center; justify-content: center; color: #94a3b8; font-style: italic; `;
const AnalysisCard = styled.div` background: white; border: 1px solid #e2e8f0; border-radius: 16px; padding: 20px; `;
const AnalysisTitle = styled.div` font-size: 13px; font-weight: 700; color: #64748b; text-transform: uppercase; `;
const PeriodToggle = styled.div` display: flex; gap: 4px; background: #f1f5f9; border-radius: 8px; padding: 4px; `;
const PeriodButton = styled.button<{ $active: boolean }>` border: none; cursor: pointer; padding: 6px 14px; border-radius: 6px; font-size: 11px; font-weight: 600; background: ${p => p.$active ? "white" : "transparent"}; color: ${p => p.$active ? theme.colors.brand.normal : "#64748b"}; box-shadow: ${p => p.$active ? "0px 1px 3px rgba(0,0,0,0.1)" : "none"}; `;
const MonthGrid = styled.div` display: flex; align-items: flex-end; gap: 12px; height: 120px; padding: 10px 0; `;
const MonthCol = styled.div` display: flex; flex-direction: column; align-items: center; gap: 8px; flex: 1; `;
const MonthBar = styled.div<{ $height: number; $hasData: boolean }>` width: 100%; max-width: 28px; height: ${p => p.$height}px; background: ${p => p.$hasData ? (theme.colors.brand.normal) : "#e5e7eb"}; border-radius: 6px 6px 2px 2px; opacity: ${p => p.$hasData ? 1 : 0.4}; transition: height 0.5s; `;
const MonthBarLabel = styled.div` font-size: 10px; font-weight: 600; color: #94a3b8; text-align: center; `;
const SlotValue = styled.div<{ $hasData: boolean }>` font-size: 10px; font-weight: ${p => p.$hasData ? 700 : 400}; color: ${p => p.$hasData ? "#1e293b" : "#94a3b8"}; `;

export default WorkDurationCard;